import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '../api/client';
import { Button } from './Button';

const contactSchema = z.object({
  name: z.string().min(2, 'Please enter your name'),
  email: z.string().email('Please enter a valid email'),
  phone: z.string().optional(),
  subject: z.string().optional(),
  message: z.string().min(10, 'Please write at least a few sentences'),
});

type ContactFormData = z.infer<typeof contactSchema>;

interface ContactFormProps {
  variant?: 'full' | 'compact';
}

const inputClasses =
  'w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none';

const labelClasses = 'block text-sm font-bold text-slate-700 mb-1.5';

const errorClasses = 'text-xs text-error mt-1';

export function ContactForm({ variant = 'compact' }: ContactFormProps) {
  const [submitted, setSubmitted] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const handleFormSubmit = async (data: ContactFormData) => {
    setSendError(null);
    try {
      await api.post('/contact', data);
      setSubmitted(true);
      reset();
    } catch (err: any) {
      setSendError(
        err?.message || 'Something went wrong. Please try emailing us directly at info@kgprodesign.com.'
      );
    }
  };

  if (submitted) {
    return (
      <div className="rounded-lg bg-success-light border border-success-border p-8 text-center">
        <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-slate-900 mb-1">Message Sent!</h3>
        <p className="text-slate-600 text-sm">
          Thanks for reaching out! The reunion team will get back to you soon.
        </p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="mt-4 text-primary text-sm font-medium hover:underline"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-5" noValidate>
      {/* Name + Email row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-name" className={labelClasses}>Your Name</label>
          <input
            id="contact-name"
            type="text"
            placeholder="First and last name"
            className={inputClasses}
            {...register('name')}
          />
          {errors.name && <p className={errorClasses}>{errors.name.message}</p>}
        </div>
        <div>
          <label htmlFor="contact-email" className={labelClasses}>Email</label>
          <input
            id="contact-email"
            type="email"
            placeholder="you@example.com"
            className={inputClasses}
            {...register('email')}
          />
          {errors.email && <p className={errorClasses}>{errors.email.message}</p>}
        </div>
      </div>

      {/* Full variant: phone + subject */}
      {variant === 'full' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="contact-phone" className={labelClasses}>Phone (optional)</label>
            <input
              id="contact-phone"
              type="tel"
              placeholder="(555) 123-4567"
              className={inputClasses}
              {...register('phone')}
            />
          </div>
          <div>
            <label htmlFor="contact-subject" className={labelClasses}>What's this about?</label>
            <input
              id="contact-subject"
              type="text"
              placeholder="Registration, payments, travel..."
              className={inputClasses}
              {...register('subject')}
            />
          </div>
        </div>
      )}

      {/* Message */}
      <div>
        <label htmlFor="contact-message" className={labelClasses}>Your Message</label>
        <textarea
          id="contact-message"
          rows={5}
          placeholder="What can we help you with? Ask us anything about the reunion..."
          className={`${inputClasses} resize-none`}
          {...register('message')}
        />
        {errors.message && <p className={errorClasses}>{errors.message.message}</p>}
      </div>

      {sendError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {sendError}
        </div>
      )}

      {/* Submit */}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        className="w-full"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Sending...' : 'Send Message'}
        {!isSubmitting && (
          <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        )}
      </Button>
    </form>
  );
}
